import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../../App';
import { environment } from '../../Env_data/environment';
import axios from 'axios';
import {
  ShieldCheck, ExternalLink, Home, BarChart3,
  Users, X, Check, Loader2, AlertTriangle,
  PlusCircle, Pencil, Sparkles
} from 'lucide-react';

const OAuthAuthorize = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useContext(AppContext) || { state: {} };
  const session = state?.session;

  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState(null);

  // Extract OAuth params from URL
  const oauthParams = {
    response_type: searchParams.get('response_type'),
    client_id: searchParams.get('client_id'),
    redirect_uri: searchParams.get('redirect_uri'),
    code_challenge: searchParams.get('code_challenge'),
    code_challenge_method: searchParams.get('code_challenge_method') || 'S256',
    state: searchParams.get('state'),
    scope: searchParams.get('scope') || 'read'
  };

  const API_BASE = environment.BASE_URL
    ? environment.BASE_URL.replace('/api/', '')
    : 'http://localhost/spent_app/backend';

  // If user is not logged in, redirect to login while preserving OAuth params
  useEffect(() => {
    if (!session?.id) {
      const currentUrl = window.location.pathname + window.location.search;
      localStorage.setItem('oauth_redirect_after_login', currentUrl);
      navigate('/login');
    }
  }, [session, navigate]);

  // Validate client_id on mount
  useEffect(() => {
    const validateClient = async () => {
      if (!oauthParams.client_id) {
        setError('Missing client_id parameter');
        setLoading(false);
        return;
      }
      if (oauthParams.response_type !== 'code') {
        setError('Unsupported response_type. Only "code" is supported.');
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(
          `${API_BASE}/mcp/api/oauth_authorize.php?client_id=${encodeURIComponent(oauthParams.client_id)}`
        );
        if (res.data.success) {
          setClientInfo(res.data.client);
        } else {
          setError(res.data.message || 'Unknown application');
        }
      } catch (err) {
        setError('Failed to validate application. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (session?.id) {
      validateClient();
    }
  }, [session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle user granting access
  const handleAllow = async () => {
    setAuthorizing(true);
    try {
      const res = await axios.post(`${API_BASE}/mcp/api/oauth_authorize.php`, {
        user_id: session.id,
        client_id: oauthParams.client_id,
        redirect_uri: oauthParams.redirect_uri,
        code_challenge: oauthParams.code_challenge,
        code_challenge_method: oauthParams.code_challenge_method,
        state: oauthParams.state,
        scope: oauthParams.scope
      });

      if (res.data.success && res.data.redirect_url) {
        // Redirect browser to the callback URL with the authorization code
        window.location.href = res.data.redirect_url;
      } else {
        setError(res.data.message || 'Failed to generate authorization code');
        setAuthorizing(false);
      }
    } catch (err) {
      setError('Authorization failed. Please try again.');
      setAuthorizing(false);
      console.error(err);
    }
  };

  // Handle user denying access
  const handleDeny = () => {
    if (oauthParams.redirect_uri) {
      let denyUrl = oauthParams.redirect_uri + '?error=access_denied';
      if (oauthParams.state) {
        denyUrl += '&state=' + encodeURIComponent(oauthParams.state);
      }
      window.location.href = denyUrl;
    } else {
      navigate(session?.homes?.length ? '/home' : '/panel');
    }
  };

  const capabilities = [
    {
      icon: <PlusCircle className="w-5 h-5 text-emerald-400" />,
      title: "Add & Log Expenses",
      desc: "Scan receipts, grocery bills, and log new household expenses",
      badge: "Write",
      badgeClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    },
    {
      icon: <Users className="w-5 h-5 text-purple-400" />,
      title: "Split Bills with Friends",
      desc: "Create shared split transactions across friends and groups",
      badge: "Write",
      badgeClass: "bg-purple-500/10 text-purple-400 border-purple-500/20"
    },
    {
      icon: <Pencil className="w-5 h-5 text-amber-400" />,
      title: "Edit & Update Expenses",
      desc: "Update details of transactions that you have added",
      badge: "Update",
      badgeClass: "bg-amber-500/10 text-amber-400 border-amber-500/20"
    },
    {
      icon: <BarChart3 className="w-5 h-5 text-blue-400" />,
      title: "Search, Filter & Analytics",
      desc: "Query expense history, category breakdowns, and spending reports",
      badge: "Read",
      badgeClass: "bg-blue-500/10 text-blue-400 border-blue-500/20"
    },
    {
      icon: <Home className="w-5 h-5 text-indigo-400" />,
      title: "Households & Friends Directory",
      desc: "View household memberships, friend list, and group contacts",
      badge: "Read",
      badgeClass: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
    }
  ];

  // Loading state
  if (!session?.id) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Validating application...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-red-500/30 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Authorization Error</h2>
          <p className="text-slate-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => navigate(session?.homes?.length ? '/home' : '/panel')}
            className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-xl transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Consent screen
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 py-8">
      <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-slate-800 p-6 text-center">
          <div className="w-16 h-16 bg-indigo-500/20 border-2 border-indigo-400/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-indigo-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1">Authorization Request</h1>
          <p className="text-slate-400 text-sm">Grant AI capabilities to your Spent App account</p>
        </div>

        {/* Application Identity */}
        <div className="p-5 border-b border-slate-800">
          <div className="bg-slate-950 rounded-2xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {clientInfo?.client_name?.charAt(0) || 'A'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white truncate">{clientInfo?.client_name || 'Unknown App'}</h3>
                <span className="inline-flex items-center gap-1 text-[10px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-1.5 py-0.5 rounded-full font-medium">
                  <Sparkles className="w-2.5 h-2.5" /> MCP
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                <ExternalLink className="w-3 h-3" />
                <span className="truncate">{oauthParams.redirect_uri || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Logged-in user */}
        <div className="px-5 py-3 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Account</span>
            <span className="text-xs text-emerald-400 font-medium">Connected</span>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 font-bold text-xs">
              {session?.firstname?.charAt(0)?.toUpperCase()}{session?.lastname?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <p className="text-white text-xs font-semibold capitalize">{session?.firstname} {session?.lastname}</p>
              <p className="text-slate-400 text-[11px]">{session?.email}</p>
            </div>
          </div>
        </div>

        {/* Permissions list */}
        <div className="px-5 py-4 border-b border-slate-800">
          <p className="text-[11px] text-slate-500 mb-3 uppercase tracking-wider font-semibold">Granted AI Capabilities</p>
          <div className="space-y-2.5">
            {capabilities.map((cap, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 bg-slate-950/60 rounded-xl border border-slate-800/80">
                <div className="p-1 rounded-lg mt-0.5 flex-shrink-0">
                  {cap.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-200">{cap.title}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${cap.badgeClass}`}>
                      {cap.badge}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="p-5 flex gap-3">
          <button
            onClick={handleDeny}
            disabled={authorizing}
            className="flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
            Deny
          </button>
          <button
            onClick={handleAllow}
            disabled={authorizing}
            className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-indigo-600/20"
          >
            {authorizing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {authorizing ? 'Authorizing...' : 'Allow Access'}
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5">
          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            By clicking "Allow Access", you authorize this application to interact with your Spent App account via MCP. You can revoke access at any time.
          </p>
        </div>

      </div>
    </div>
  );
};

export default OAuthAuthorize;
